import CampaignManagerFilterRow from './filter-row';
import CampaignTable from './campaign-table';
import { CampaignWrapper } from './styled';
import { errorToast } from '../../utils/toaster';
import {
  ERROR_MESSAGES,
  TOAST_TIMEOUTS,
  REDUCER_ACTIONS,
} from '../../constants/campaigns';
import { useEffect } from 'react';
import { uniqBy } from 'lodash';
import { fetchLineContextsForRecordings } from '../../actions/line-contexts';
import { fetchAreaContextsForRecordings } from '../../actions/area-contexts';
import useLoadingHandler from '../../hooks/use-loading-handler';
import {
  convertFromUrlHashToObject,
} from '../../utils/urlHelpers';
import {
  generateTaxonomyListFromContexts,
} from '../../utils/campaignHelpers';
import { useFilterOptionsContext } from './useFilterOptionsContext';
import moment from 'moment'; // Import moment library
import { apiEndpointByMetricKey } from '../../constants/api-endpoints';
import { buildPayload, getQueryPayloadType } from '../../services/query-api';
import { fetchSQLQuery } from '../../services/sql-api';
import { withTheme } from 'styled-components';

/**
 * Campaign Manager Component
 */
const CampaignManagerComponent = ({
  campaign,
  locations,
  recordings,
  showStaffFilters,
  disableAgeAndGenderOnFrontEnd,
  availableMetrics,
  saveCampaign,
}) => {
  const { updateFilterOptions, state, ctxFetched } = useFilterOptionsContext();
  const { isLoading, startLoading, stopLoading } = useLoadingHandler();

  // Set filter options with campaign prop data on load
  useEffect(() => {
    if (campaign) {
      updateFilterOptions(REDUCER_ACTIONS.SET_FILTER_OPTIONS, campaign);
    }
    // Only run when campaign changes
  }, [campaign]);

  // Handle initial data fetching and hash conversion
  useEffect(() => {
    if (!ctxFetched.current) {
      getContexts();

      // After contexts are loaded, handle hash if present
      if (window.location.hash) {
        const hashedState = convertFromUrlHashToObject(window.location.hash);
        if (hashedState) {
          updateFilterOptions(REDUCER_ACTIONS.SET_FILTER_OPTIONS, hashedState);
        }
      }
    }
  }, []);

  const getContexts = async () => {
    const fetchKey = 'fetch-contexts';
    try {
      startLoading(fetchKey);
      const lineContexts = await fetchLineContextsForRecordings(
        recordings.map((rec) => rec.id),
      );
      const areaContexts = await fetchAreaContextsForRecordings(
        recordings.map((rec) => rec.id),
      );

      const areaContextsFormat = [];
      Array.from(areaContexts)
        .map(
          (ctx) => ctx[1].length > 0 && ctx[1].filter((item) => item.taxonomy),
        )
        .filter((item) => item !== false)
        .forEach((item) => {
          areaContextsFormat.push(...item);
        });

      const lineContextsFormat = [];
      Array.from(lineContexts)
        .map(
          (ctx) => ctx[1].length > 0 && ctx[1].filter((item) => item.taxonomy),
        )
        .filter((item) => item !== false)
        .forEach((item) => {
          lineContextsFormat.push(...item);
        });

      const allContextsFormat = [...areaContextsFormat, ...lineContextsFormat];

      updateFilterOptions(REDUCER_ACTIONS.SET_CTX, allContextsFormat);
      ctxFetched.current = true;

      if (areaContexts) {
        updateFilterOptions(
          REDUCER_ACTIONS.SET_TAXONOMY_LIST,
          generateTaxonomyListFromContexts(areaContextsFormat),
        );
        updateFilterOptions(
          REDUCER_ACTIONS.SET_TAXONOMY_KEY_STRINGS,
          uniqBy(areaContextsFormat, 'taxonomy')
            .filter((c) => c.taxonomy.includes(':')) // for demo dash, ignore taxonomies that don't have a parent
            .map((c) => c.taxonomy)
            .sort((a, b) => a.localeCompare(b, 'en', { numeric: true })),
        );
      }

      if (lineContexts) {
        updateFilterOptions(
          REDUCER_ACTIONS.SET_LINE_TAXONOMY_LIST,
          generateTaxonomyListFromContexts(lineContextsFormat),
        );
        updateFilterOptions(
          REDUCER_ACTIONS.SET_LINE_TAXONOMY_KEY_STRINGS,
          uniqBy(lineContextsFormat, 'taxonomy')
            .filter((c) => c.taxonomy.includes(':')) // for demo dash, ignore taxonomies that don't have a parent
            .map((c) => c.taxonomy)
            .sort((a, b) => a.localeCompare(b, 'en', { numeric: true })),
        );
      }
    } catch (error) {
      errorToast({
        message: ERROR_MESSAGES.FETCH_ERROR,
        timeout: TOAST_TIMEOUTS.SHORT,
      });
    } finally {
      stopLoading(fetchKey);
    }
  };

  // Handle enabled locations updates
  useEffect(() => {
    if (!isLoading) {
      handleEnabledLocations(locations, state.filterOptions);
    }
  }, [state.filterOptions.taxonomies, state.filterOptions.lineTaxonomies, state.filterOptions.metric, locations]);

  const handleEnabledLocations = (locations, filterOptions) => {
    // when selecting a taxonomy we want to enable all locations that have that taxonomy
    // for areas
    if (
      filterOptions.metric.type === 'area' &&
      filterOptions?.taxonomies &&
      filterOptions?.taxonomies.length > 0
    ) {
      const locationsWithTaxonomy = state.ctx
        .filter((c) => filterOptions.taxonomies.includes(c.taxonomy))
        .map((c) => c.location_id);

      const enabledLocations = locations
        .filter((loc) => locationsWithTaxonomy.includes(loc.id))
        .map((loc) => loc.id);

      updateFilterOptions(
        REDUCER_ACTIONS.SET_ENABLED_LOCATIONS,
        enabledLocations,
      );
      return;
    }
    // or for lines
    if (
      filterOptions.metric.type === 'line' &&
      filterOptions?.lineTaxonomies &&
      filterOptions?.lineTaxonomies.length > 0
    ) {
      const locationsWithTaxonomy = state.ctx
        .filter((c) => filterOptions.lineTaxonomies.includes(c.taxonomy))
        .map((c) => c.location_id);
      updateFilterOptions(
        REDUCER_ACTIONS.SET_ENABLED_LOCATIONS,
        locations
          .filter((loc) => locationsWithTaxonomy.includes(loc.id))
          .map((loc) => loc.id),
      );
      return;
    }
    // or just set all locations as enabled
    if (locations.length !== state.enabledLocations.length) {
      updateFilterOptions(
        REDUCER_ACTIONS.SET_ENABLED_LOCATIONS,
        locations.map((loc) => loc.id),
      );
    }
  };

  useEffect(() => {
    if (!state.filterOptions.metric) {
      errorToast({
        message: ERROR_MESSAGES.NO_METRIC,
        timeout: TOAST_TIMEOUTS.SHORT,
      });
      return;
    }
    if (!state.filterOptions.testLocations.length > 0) {
      errorToast({
        message: ERROR_MESSAGES.NO_TEST_LOCATIONS,
        timeout: TOAST_TIMEOUTS.SHORT,
      });
      return;
    }
    if (!state.filterOptions.testPeriod) {
      errorToast({
        message: ERROR_MESSAGES.NO_TEST_PERIOD,
        timeout: TOAST_TIMEOUTS.SHORT,
      });
      return;
    }
    if (
      state.taxonomyKeyStrings.length > 0 &&
      state.filterOptions.metric.type === 'area' &&
      state.filterOptions.taxonomies.length === 0
    ) {
      errorToast({
        message: ERROR_MESSAGES.NO_TAXONOMIES_SELECTED,
        timeout: TOAST_TIMEOUTS.LONG,
      });
      return;
    }
    if (
      state.filterOptions.controlLocations.length === 0 &&
      !state.filterOptions.controlPeriod?.active
    ) {
      errorToast({
        message: ERROR_MESSAGES.NO_CONTROL,
        timeout: TOAST_TIMEOUTS.VERY_LONG,
      });
      return;
    }

    updateFilterOptions(REDUCER_ACTIONS.CLEAR_DATA);

    const promises = [];

    promises.push(
      loadWidgetData(
        state.filterOptions,
        REDUCER_ACTIONS.SET_DATA_TEST_TEST,
        state.filterOptions.testLocations,
        state.filterOptions.testPeriod,
      ),
    );

    if (
      state.filterOptions.controlPeriod &&
      state.filterOptions.controlPeriod.active
    ) {
      promises.push(
        loadWidgetData(
          state.filterOptions,
          REDUCER_ACTIONS.SET_DATA_TEST_CONTROL,
          state.filterOptions.testLocations,
          state.filterOptions.controlPeriod,
        ),
      );
    }

    if (
      state.filterOptions.controlLocations &&
      state.filterOptions.controlLocations.length > 0
    ) {
      promises.push(
        loadWidgetData(
          state.filterOptions,
          REDUCER_ACTIONS.SET_DATA_CONTROL_TEST,
          state.filterOptions.controlLocations,
          state.filterOptions.testPeriod,
        ),
      );
    }

    if (
      state.filterOptions.controlPeriod &&
      state.filterOptions.controlPeriod.active &&
      state.filterOptions.controlLocations &&
      state.filterOptions.controlLocations.length > 0
    ) {
      promises.push(
        loadWidgetData(
          state.filterOptions,
          REDUCER_ACTIONS.SET_DATA_CONTROL_CONTROL,
          state.filterOptions.controlLocations,
          state.filterOptions.controlPeriod,
        ),
      );
    }
    try {
      Promise.all(promises).then((res) => {
        const data = {};
        res.forEach(({ action, response, metric, payloadInput }) => {
          if (response && response.summary && response.summary[0]) {
            const key = {
              [REDUCER_ACTIONS.SET_DATA_TEST_TEST]: 'test_test',
              [REDUCER_ACTIONS.SET_DATA_TEST_CONTROL]: 'test_control',
              [REDUCER_ACTIONS.SET_DATA_CONTROL_TEST]: 'control_test',
              [REDUCER_ACTIONS.SET_DATA_CONTROL_CONTROL]: 'control_control',
            };

            let value = response.summary[0][metric.key];

            if (metric.unitType === 'number') {
              // average per location, per day
              // ensures comparison is fair across different number of locations and days

              const locationCount = payloadInput.locations.length;
              const dayCount = payloadInput.period?.selectedDates
                ? moment(payloadInput.period.selectedDates.end).diff(
                    moment(payloadInput.period.selectedDates.start),
                    'days',
                  ) + 1
                : 1;

              value = value / locationCount / dayCount;
            }

            data[key[action]] = value;
          } else {
            console.warn(`No summary data for action: ${action}`);
          }
        });
        
        updateFilterOptions(REDUCER_ACTIONS.SET_DATA, data);
        updateFilterOptions(REDUCER_ACTIONS.SET_DATA_UPLIFT);

        // setLoading(false);
        // setResponse(response);
      });
    } catch (e) {
      console.warn(e);
      // setError(true);
      // setLoading(false);
    }
  }, [
    state.filterOptions.metric,
    state.filterOptions.testLocations,
    state.filterOptions.testPeriod,
    state.filterOptions.controlLocations,
    state.filterOptions.controlPeriod,
    state.filterOptions.taxonomies,
    state.filterOptions.lineTaxonomies,
    state.filterOptions.selectedBreakdowns,
  ]);

  const loadWidgetData = async (filterOptions, action, locations, period) => {
    // setError(false);
    // setLoading(true);
    // setDataAvailable(true);

    const metric = filterOptions.metric;
    const metricKey = metric.key;
    const endpoint = apiEndpointByMetricKey(metricKey);

    let taxonomies = undefined;
    switch (metric.type) {
      case 'area':
        taxonomies = filterOptions.taxonomies;
        break;
      case 'line':
        taxonomies = filterOptions.lineTaxonomies;
        break;
    }

    const payloadInput = {
      period,
      locations,
      taxonomies,
      aggregation: 'day',
      // todo, move roles, genders, ages request format to metric model in filterOptions
      roles: filterOptions.selectedBreakdowns.filter(
        (item) => item.key === 'roles',
      )[0]?.values || ['customer'],
      genders:
        filterOptions.selectedBreakdowns.filter(
          (item) => item.key === 'genders',
        )[0]?.values || undefined,
      ages:
        filterOptions.selectedBreakdowns
          .filter((item) => item.key === 'ages')[0]
          ?.values.map((k) => k.replaceAll('-', '_')) || undefined,
      breakdownByDimensions: undefined,
      facets: ['summary'],
    };

    const queryPayloadType = getQueryPayloadType(endpoint, {
      url: endpoint,
    });

    // Create a new promise that includes the action with the query result
    return new Promise((resolve, reject) => {
      const key = `fetch-query-data-${action}`;
      startLoading(key);
      fetchSQLQuery(buildPayload(queryPayloadType, payloadInput), endpoint, {
        returnErrors: true,
        return404AsError: false,
        metricKey,
      })
        .then((response) => {
          resolve({ action, response, metric, payloadInput });
        })
        .catch((error) => {
          reject({ action, error, metric, payloadInput });
        })
        .finally(() => {
          stopLoading(key);
        });
    });
  };

  return (
    <CampaignWrapper>
      <CampaignManagerFilterRow
        locations={locations}
        enabledLocations={locations.map((l) => l.id)}
        recordings={recordings}
        taxonomies={state.filterOptions?.taxonomies || []}
        taxonomyKeyStrings={state?.taxonomyKeyStrings || []}
        showStaff={showStaffFilters}
        disableAgeAndGenderOnFrontEnd={disableAgeAndGenderOnFrontEnd}
        isLoading={isLoading}
        availableMetrics={availableMetrics}
        saveCampaign={saveCampaign}
      />
      <CampaignTable />
      {/* <CampaignGraph
        locations={locations}
        showStaffFilters={showStaffFilters}
        disableAgeAndGenderOnFrontEnd={disableAgeAndGenderOnFrontEnd}
        settings={settings}
        fetchingQueryData={fetchingQueryData}
        queryResponseList={queryResponseList}
        availableMetrics={availableMetrics}
        fetchQueryResponses={fetchQueryResponses}
        theme={theme}
      /> */}
    </CampaignWrapper>
  );
};

export default withTheme(CampaignManagerComponent);
